import type { FieldPack, FieldManifest } from '../types';

export class FieldRegistry {
  private static instance: FieldRegistry;
  private packs: Map<string, FieldPack> = new Map();
  private fieldIndex: Map<string, FieldManifest> = new Map();

  private constructor() {}

  static getInstance(): FieldRegistry {
    if (!FieldRegistry.instance) {
      FieldRegistry.instance = new FieldRegistry();
    }
    return FieldRegistry.instance;
  }

  registerPack(pack: FieldPack): void {
    if (pack.dependencies) {
      for (const depId of pack.dependencies) {
        if (!this.packs.has(depId)) {
          throw new Error(`Field pack "${pack.id}" depends on "${depId}" which is not registered`);
        }
      }
    }

    const visited = new Set<string>();
    const visiting = new Set<string>();

    const detectCycle = (packId: string): boolean => {
      if (visiting.has(packId)) return true;
      if (visited.has(packId)) return false;

      visiting.add(packId);
      const currentPack = this.packs.get(packId);

      if (currentPack?.dependencies) {
        for (const depId of currentPack.dependencies) {
          if (detectCycle(depId)) return true;
        }
      }

      visiting.delete(packId);
      visited.add(packId);
      return false;
    };

    if (pack.dependencies) {
      for (const depId of pack.dependencies) {
        if (detectCycle(depId)) {
          throw new Error(`Circular dependency detected involving pack "${pack.id}"`);
        }
      }
    }

    this.packs.set(pack.id, pack);
    pack.fields.forEach(field => {
      const key = field.namespace ? `${field.namespace}.${field.id}` : field.id;
      this.fieldIndex.set(key, field);
    });
  }

  unregisterPack(packId: string): void {
    const pack = this.packs.get(packId);
    if (pack) {
      pack.fields.forEach(field => {
        const key = field.namespace ? `${field.namespace}.${field.id}` : field.id;
        this.fieldIndex.delete(key);
      });
      this.packs.delete(packId);
    }
  }

  getPack(packId: string): FieldPack | undefined {
    return this.packs.get(packId);
  }

  getAllPacks(): FieldPack[] {
    return Array.from(this.packs.values());
  }

  getField(fieldId: string, namespace?: string): FieldManifest | undefined {
    const key = namespace ? `${namespace}.${fieldId}` : fieldId;
    return this.fieldIndex.get(key);
  }

  getFieldsFromPacks(packIds: string[]): FieldManifest[] {
    const fields: FieldManifest[] = [];
    const processedPacks = new Set<string>();

    const processPack = (packId: string) => {
      if (processedPacks.has(packId)) return;
      processedPacks.add(packId);

      const pack = this.packs.get(packId);
      if (!pack) return;

      if (pack.dependencies) {
        pack.dependencies.forEach(depId => processPack(depId));
      }

      fields.push(...pack.fields);
    };

    packIds.forEach(packId => processPack(packId));
    return fields;
  }

  getFieldsByTag(tagId: string, packIds?: string[]): FieldManifest[] {
    const fieldsToSearch = packIds
      ? this.getFieldsFromPacks(packIds)
      : Array.from(this.fieldIndex.values());

    return fieldsToSearch.filter(field => {
      if (Array.isArray(field.tags)) {
        return field.tags.some(tag =>
          typeof tag === 'string' ? tag === tagId : tag.id === tagId
        );
      }
      return false;
    });
  }

  hasPack(packId: string): boolean {
    return this.packs.has(packId);
  }

  clear(): void {
    this.packs.clear();
    this.fieldIndex.clear();
  }
}

export const fieldRegistry = FieldRegistry.getInstance();
